import { Link, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { ShoppingBag, Heart, User, Menu, X, LogOut, Search, Package, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const { user, isAuthenticated, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
      setMobileMenuOpen(false);
    }
  };

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
    if (!searchOpen) {
      // Focus input when opening
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  };

  // Close search on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="header">
      <div className="container-luxury">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="/aliglam-logo.png" 
              alt="Aliglam" 
              className="h-16 md:h-20 w-auto object-contain"
              data-testid="logo-image"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-12" data-testid="desktop-nav">
            <Link 
              to="/" 
              className="text-sm tracking-wide text-[#57534E] hover:text-[#BA7B7C] transition-colors"
              data-testid="nav-home"
            >
              Inicio
            </Link>
            <Link 
              to="/shop" 
              className="text-sm tracking-wide text-[#57534E] hover:text-[#BA7B7C] transition-colors"
              data-testid="nav-shop"
            >
              Tienda
            </Link>
            <Link 
              to="/glam-advisor" 
              className="text-sm tracking-wide text-[#57534E] hover:text-[#BA7B7C] transition-colors flex items-center space-x-1"
              data-testid="nav-advisor"
            >
              <Sparkles className="w-4 h-4" />
              <span>AI Advisor</span>
            </Link>
            <Link 
              to="/shop?category=Perfumes" 
              className="text-sm tracking-wide text-[#57534E] hover:text-[#BA7B7C] transition-colors"
              data-testid="nav-perfumes"
            >
              Perfumes
            </Link>
          </nav>

          {/* Right Icons */}
          <div className="flex items-center space-x-6">
            {/* Search Button - Desktop */}
            <button
              onClick={toggleSearch}
              className="hidden md:flex text-[#57534E] hover:text-[#BA7B7C] transition-colors"
              data-testid="search-btn"
              aria-label="Buscar"
            >
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </button>
            {isAuthenticated ? (
              <>
                <Link 
                  to="/wishlist" 
                  className="relative text-[#57534E] hover:text-[#BA7B7C] transition-colors"
                  data-testid="wishlist-link"
                >
                  <Heart className="w-5 h-5" strokeWidth={1.5} />
                </Link>
                <Link 
                  to="/cart" 
                  className="relative text-[#57534E] hover:text-[#BA7B7C] transition-colors"
                  data-testid="cart-link"
                >
                  <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                  {cart.item_count > 0 && (
                    <span className="cart-badge" data-testid="cart-count">
                      {cart.item_count}
                    </span>
                  )}
                </Link>
                <div className="relative group">
                  <button 
                    className="flex items-center space-x-2 text-[#57534E] hover:text-[#BA7B7C] transition-colors"
                    data-testid="user-menu-btn"
                  >
                    <User className="w-5 h-5" strokeWidth={1.5} />
                    <span className="hidden md:inline text-sm">{user?.name?.split(' ')[0]}</span>
                  </button>
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                    <div className="px-4 py-2 border-b border-[#E7E5E4]">
                      <p className="text-sm font-medium text-[#1C1917]">{user?.name}</p>
                      <p className="text-xs text-[#A8A29E]">{user?.email}</p>
                    </div>
                    <Link 
                      to="/orders"
                      className="w-full px-4 py-2 text-left text-sm text-[#57534E] hover:bg-[#F5F0EB] flex items-center space-x-2"
                      data-testid="orders-link"
                    >
                      <Package className="w-4 h-4" />
                      <span>Mis pedidos</span>
                    </Link>
                    <button 
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-[#57534E] hover:bg-[#F5F0EB] flex items-center space-x-2"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Cerrar sesión</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <Link 
                to="/login" 
                className="text-sm tracking-wide text-[#57534E] hover:text-[#BA7B7C] transition-colors flex items-center space-x-2"
                data-testid="login-link"
              >
                <User className="w-5 h-5" strokeWidth={1.5} />
                <span className="hidden md:inline">Iniciar sesión</span>
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-[#57534E]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-[#E7E5E4]" data-testid="mobile-menu">
          <nav className="container-luxury py-6 space-y-4">
            <Link 
              to="/" 
              className="block text-lg text-[#1C1917] hover:text-[#BA7B7C]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Inicio
            </Link>
            <Link 
              to="/shop" 
              className="block text-lg text-[#1C1917] hover:text-[#BA7B7C]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Tienda
            </Link>
            <Link 
              to="/glam-advisor" 
              className="block text-lg text-[#1C1917] hover:text-[#BA7B7C] flex items-center space-x-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-advisor-link"
            >
              <Sparkles className="w-5 h-5 text-[#BA7B7C]" />
              <span>AI Advisor</span>
            </Link>
            <Link 
              to="/shop?category=Perfumes" 
              className="block text-lg text-[#1C1917] hover:text-[#BA7B7C]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Perfumes
            </Link>
            <Link 
              to="/shop?category=Rubores" 
              className="block text-lg text-[#1C1917] hover:text-[#BA7B7C]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Rubores
            </Link>
            
            {/* Mobile Orders Link - Only for authenticated users */}
            {isAuthenticated && (
              <Link 
                to="/orders" 
                className="block text-lg text-[#1C1917] hover:text-[#BA7B7C] flex items-center space-x-2"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-orders-link"
              >
                <Package className="w-5 h-5" />
                <span>Mis pedidos</span>
              </Link>
            )}
            
            {/* Mobile Search */}
            <form onSubmit={handleSearchSubmit} className="pt-4 border-t border-[#E7E5E4] mt-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar productos..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-[#E7E5E4] focus:outline-none focus:border-[#BA7B7C] transition-colors text-[#1C1917]"
                  data-testid="mobile-search-input"
                />
              </div>
            </form>
          </nav>
        </div>
      )}

      {/* Desktop Search Overlay */}
      {searchOpen && (
        <div className="hidden md:block absolute top-full left-0 right-0 bg-white border-t border-[#E7E5E4] shadow-lg" data-testid="search-overlay">
          <div className="container-luxury py-6">
            <form onSubmit={handleSearchSubmit} className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar productos..."
                  className="w-full pl-14 pr-14 py-4 rounded-2xl border border-[#E7E5E4] focus:outline-none focus:border-[#BA7B7C] transition-colors text-lg"
                  data-testid="desktop-search-input"
                />
                <button
                  type="button"
                  onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                  className="absolute right-5 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-[#57534E] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-center text-sm text-[#A8A29E] mt-3">
                Presiona Enter para buscar o Esc para cerrar
              </p>
            </form>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
